import * as React from "react";
import { useCallback, useEffect } from "react";
import styled from "@mui/material/styles/styled";
import { GenericButton } from "./uiControllers/genericButton";
import { Checkbox, Fade, FormControlLabel, Link } from "@mui/material";
import { PatientRoute } from "../PatientRoutes/PatientRoute";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { ReactComponent as CheckedCheckbox } from "../assets/checkedCheckbox.svg";
import { ReactComponent as EmptyCheckbox } from "../assets/emptyCheckbox.svg";
import { usePatientRouteChanger } from "../PatientRoutes/routeChanger";
import { useTranslate } from "../hooks/useTranslate";
import { useNavigation } from "../state/navigationContext";
import { useWidgetSettings } from "../widget/useWidgetSettings";
import { PatientColorMap } from "../layout/patientColorMap";
import { usePatient } from "../state/patientContext";
import { usePatientFeatureFlags } from "../hooks/usePatientFeatureFlags";
import { useRefreshRoles } from "../hooks/useRefreshRoles";
import { HtmlOrTextContent } from "./uiControllers/HtmlOrTextContent";
import {
  marginLeftToMarginRightOnRtl,
  onRtl,
} from "fe-web-components/rtl/rtlStyles";
import {
  WaveArea,
  WaveAreaAlignment,
  WaveViewMainContent,
} from "../style/waveStyles";
import { useClinicData } from "../clinic/clinicHooks";
import { LanguageChanger } from "../layout/languageChanger";
import { PatientThemeImage } from "../themes/PatientThemeImage";

//region [[ Styles ]]

const TermsOfServiceContainerView = styled("div")({
  display: "flex",
  flexFlow: "column",
  height: "100%",
  width: "100%",
  position: "absolute",
});

const BottomArea = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-evenly",
  alignItems: "center",
  flexDirection: "column",
  height: "100%",
  padding: "0",
  "& .get-started-button": {
    marginBottom: "10px",
    width: "280px",
  },
  // make button smaller on tiny screens
  [theme.breakpoints.up("sm")]: {
    "& .get-started-button": {
      with: "300px",
    },
    padding: "0 25px",
  },
}));

const LinkSection = styled("div")({
  marginTop: "10px",
  fontSize: "14px",
  color: PatientColorMap.HomepageLinkColor,
  display: "flex",
  alignItems: "end",
});

const VisitText = styled("div")({
  marginTop: "1px",
  marginLeft: 0,
  marginRight: 10,
  ...onRtl({
    marginRight: 0,
  }),
});

const LogoArea = styled("div")({
  display: "flex",
  alignItems: "center",
  paddingBottom: "20px",
  "& img": {
    maxWidth: "300px",
    minHeight: "50px",
  },
});

const HeaderTop = styled("div")({
  paddingBottom: "14px",
});

const TermsOfServiceText = styled("div")({
  fontSize: "17px",
  fontWeight: 500,
  whiteSpace: "break-spaces",
  paddingBottom: "5px",
});

const WelcomeTitle = styled("div")({
  fontSize: "17px",
  fontWeight: 700,
  // color: "white",
  marginTop: "10px",
  paddingBottom: "0px",
});

const TermsOfServiceAgreementContainer = styled("div")({
  color: PatientColorMap.Default,
  fontSize: "12px",
  fontWeight: 500,
  padding: "10px",
  // paddingRight: "25px",
  "& a": {
    color: PatientColorMap.TermsLinkColor,
    textDecoration: "underline",
  },
});

const TermsOfServiceActions = styled("div")({
  fontSize: "14px",
  fontWeight: 500,
  paddingRight: "14px",
});

const AgreeOnTermsFormControlLabel = styled(FormControlLabel)({
  marginLeft: 5,
  marginRight: 5,
});

const WelcomeText = styled("div")({
  fontSize: "21px",
  fontWeight: 600,
  color: PatientColorMap.HomepageTextColor,
  ...marginLeftToMarginRightOnRtl(15),
});

const WebsiteLink = styled(Link)({
  color: PatientColorMap.HomepageLinkColor,
  textDecoration: "underline",
});

const StyledLink = styled(Link)({
  color: PatientColorMap.TermsLinkColor,
  textDecoration: "underline",
});

const KahunLogo = styled("div")(({ theme }) => ({
  marginRight: "5px",
  //paddingBottom: "30px",
}));

const TermsCheckBox = styled(Checkbox)({
  "& input": {
    display: "none",
  },
});

const StyledCheckedCheckbox = styled(CheckedCheckbox)({
  "& [fill]": {
    fill: PatientColorMap.Primary,
  },
  "& [stroke]": {
    stroke: PatientColorMap.Primary,
  },
});

const StyledEmptyCheckbox = styled(EmptyCheckbox)({
  fill: PatientColorMap.Disabled,
  "& [stroke]": {
    stroke: PatientColorMap.Primary,
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface TermsOfServiceContainerProps {
  onContinue: () => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]

const termsOfUseURL = "https://kahun.com/terms-of-service/";
const privacyPolicyURL = "https://kahun.com/privacy-policy/";
const cookiePolicyURL = "https://kahun.com/cookie-policy/";

//endregion [[ Functions ]]

export const WelcomeContainer = ({
  ...props
}: TermsOfServiceContainerProps) => {
  const routeChanger = usePatientRouteChanger();

  const { themeSettings } = useNavigation();

  const { agreeOnTerms, setAgreeOnTerms } = usePatient();

  const standaloneWelcome = usePatientFeatureFlags("standaloneWelcome");

  useRefreshRoles();

  const onContinue = () => {
    if (agreeOnTerms) {
      routeChanger(PatientRoute.Questions);
    }
  };

  const { partnerId } = useWidgetSettings();

  const { translate } = useTranslate();

  const toggleAgree = useCallback(
    (e) => {
      const { checked } = e?.target || {};
      setAgreeOnTerms(checked);
    },
    [setAgreeOnTerms],
  );

  const { name: clinicName, clinic: clinicId } = useClinicData();

  useEffect(() => {
    evt({
      category: CATEGORY.PATIENT_INIT,
      action: ACTION.PATIENT_PAGE_LOAD,
      label: "Terms of service page",
      partnerId,
    });
  }, [partnerId, clinicId, clinicName]);

  const isGradientEnabled = themeSettings ? themeSettings.gradient : "false";

  return (
    <TermsOfServiceContainerView>
      <WaveViewMainContent
        color={PatientColorMap.GradLight}
        textcolor={PatientColorMap.HomepageTextColor}
        backgroundimg={themeSettings?.homepageBackgroundImageUrl}
        gradient={isGradientEnabled ? "true" : "false"}
        key={"header " + isGradientEnabled}
        bottom_wave={"true"} // otherwise we get a warning
      >
        <HeaderTop>
          <LanguageChanger />
          <LogoArea>
            {themeSettings && (
              <PatientThemeImage source={themeSettings.mainLogo} />
            )}
            <WelcomeText>
              <HtmlOrTextContent content={translate("WELCOME_LOGO")} />
            </WelcomeText>
          </LogoArea>
          <TermsOfServiceText>
            <HtmlOrTextContent content={translate("WELCOME_PATIENT")} />
          </TermsOfServiceText>
          <TermsOfServiceText>
            {!standaloneWelcome ? (
              <HtmlOrTextContent
                content={`${translate("WELCOME_PARTNERS_1")}\n\n${translate(
                  "WELCOME_PARTNERS_2",
                )}\n\n${translate("WELCOME_PARTNERS_3")}`}
              />
            ) : (
              translate("WELCOME_STANDALONE")
            )}
          </TermsOfServiceText>
          <WelcomeTitle>
            <HtmlOrTextContent content={translate("WELCOME_TITLE")} />
          </WelcomeTitle>
          <LinkSection>
            <VisitText>{translate("VISIT")}</VisitText>
            {themeSettings &&
            (themeSettings.showKahunLink?.toString?.() === "true" ||
              themeSettings.showKahunLink === true) ? (
              <WebsiteLink href={"http://www.kahun.com"}>
                {translate("KAHUN_WEBSITE")}
              </WebsiteLink>
            ) : (
              <WebsiteLink href={"http://www.kahun.com"}>
                <KahunLogo>
                  {themeSettings && (
                    <PatientThemeImage source={themeSettings.kahunLogo} />
                  )}
                </KahunLogo>
              </WebsiteLink>
            )}
          </LinkSection>
        </HeaderTop>
      </WaveViewMainContent>
      <WaveArea alignment={WaveAreaAlignment.Bottom}>
        <BottomArea>
          <TermsOfServiceActions>
            <AgreeOnTermsFormControlLabel
              control={
                <TermsCheckBox
                  checked={agreeOnTerms}
                  onChange={toggleAgree}
                  checkedIcon={<StyledCheckedCheckbox />}
                  icon={<StyledEmptyCheckbox />}
                />
              }
              label={
                <TermsOfServiceAgreementContainer>
                  {" "}
                  <HtmlOrTextContent content={translate("HAVE_READ")} />
                  <StyledLink target="_blank" href={termsOfUseURL}>
                    <HtmlOrTextContent content={translate("TERMS_OF_USE")} />
                  </StyledLink>
                  ,{" "}
                  <StyledLink target="_blank" href={privacyPolicyURL}>
                    <HtmlOrTextContent content={translate("PRIVACY_POLICY")} />
                  </StyledLink>{" "}
                  <HtmlOrTextContent content={translate("AND")} />{" "}
                  <StyledLink target="_blank" href={cookiePolicyURL}>
                    <HtmlOrTextContent content={translate("COOKIE_POLICY")} />
                  </StyledLink>
                </TermsOfServiceAgreementContainer>
              }
            />
          </TermsOfServiceActions>
          <Fade in mountOnEnter unmountOnExit>
            <GenericButton
              onClick={onContinue}
              label={translate("GET_STARTED")}
              disabled={!agreeOnTerms}
              className={"get-started-button"}
            />
          </Fade>
        </BottomArea>
      </WaveArea>
    </TermsOfServiceContainerView>
  );
};
